import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import routes from '@/app/routes';
import { TimelineItem, Button, AlertDialog } from '@yaradigitallabs/ahua-react';
import {
  bulkImportAlertErrorText,
  bulkImportAlertSuccessText,
} from './bulkImportConstants';

type BulkImportAlertProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  error: boolean;
  csvContent?: string | null;
};

const BulkImportAlert = ({
  open,
  onOpenChange,
  error,
  csvContent = null,
}: BulkImportAlertProps) => {
  const [IsOpen, setIsOpen] = useState<boolean>(open);
  const navigate = useNavigate();

  useEffect(() => {
    setIsOpen(open);
  }, [open]);

  const handleDialogOpenChange = (nextOpen: boolean) => {
    setIsOpen(nextOpen);
    onOpenChange(nextOpen);
  };

  const handleDownloadCsv = () => {
    if (!csvContent) {
      return;
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const downloadUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');

    anchor.href = downloadUrl;
    anchor.download = `bulk-import-errors-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(downloadUrl);

    handleDialogOpenChange(false);
  };

  const handleImportHistoryNavigate = () => {
    navigate(routes.BULK_IMPORTS_HISTORY);
    handleDialogOpenChange(false);
  };

  return (
    <AlertDialog
      open={IsOpen}
      onOpenChange={handleDialogOpenChange}
      align="center"
      colorConcept="success"
      iconSrc="Check"
      type="icon"
    >
      <AlertDialog.Content>
        <AlertDialog.Start>
          <AlertDialog.Media
            align="center"
            colorConcept={error ? 'destructive' : 'success'}
            iconSrc={error ? 'Bang' : 'Check'}
            type="icon"
          />
        </AlertDialog.Start>
        <AlertDialog.Middle>
          <AlertDialog.Title align="center">
            {error
              ? bulkImportAlertErrorText.TITLE
              : bulkImportAlertSuccessText.TITLE}
          </AlertDialog.Title>
          <AlertDialog.Description align="center">
            {error
              ? bulkImportAlertErrorText.DESCRIPTION
              : bulkImportAlertSuccessText.DESCRIPTION}
          </AlertDialog.Description>
        </AlertDialog.Middle>
        <AlertDialog.End>
          <TimelineItem.ButtonGroup
            buttonWidth="block"
            orientation="vertical"
            size="s"
          >
            {!error ? (
              <Button onClick={handleImportHistoryNavigate}>
                {bulkImportAlertSuccessText.CHECK_IMPORTS_HISTORY_BUTTON_TEXT}
              </Button>
            ) : (
              <Button onClick={handleDownloadCsv} disabled={!csvContent}>
                {bulkImportAlertErrorText.DOWNLOAD_CSV_BUTTON_TEXT}
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={() => {
                setIsOpen(false);
                onOpenChange(false);
              }}
            >
              {bulkImportAlertSuccessText.CANCEL_BUTTON_TEXT}
            </Button>
          </TimelineItem.ButtonGroup>
        </AlertDialog.End>
      </AlertDialog.Content>
    </AlertDialog>
  );
};

export default BulkImportAlert;
